import { skipToken } from '@reduxjs/toolkit/query';
import { useGetVehicleId } from 'pages/CreateVehicle/hooks/useGetVehicleId';
import { useGetSectionsQuery } from 'services/api/vehicle-sections';
import { Section } from 'services/api/vehicle-sections/types';
import { useLevel1SectionData } from './useLevel1Section';
import { useExteriorSectionData } from './useExteriorSection';
import { useViewedVehicle } from 'pages/Admin/ViewVehicle/hooks/useViewedVehicle';

export const useDriveCyclesSection = (): Section | undefined => {
  const vehicleId = useGetVehicleId();
  const { data: sections } = useGetSectionsQuery(vehicleId || skipToken);
  const { subSections: dcSubs } = useLevel1SectionData('drive_cycles');
  const { subSections: extSubs } = useExteriorSectionData('exterior');
  const vehicle = useViewedVehicle();

  const isManual = !!vehicle?.transmission?.toLowerCase().startsWith('manual');
  const is4WD = vehicle?.drive_train?.toLowerCase() === '4wd';

  const candidates: Array<[string, Section | undefined]> = [
    ['how_vehicle_runs_overall', dcSubs.how_vehicle_runs_overall],
    ['engine_noises', dcSubs.engine_noises],
    ['how_transmission_shifts', dcSubs.how_transmission_shifts],
    ['transmission_shudder', dcSubs.transmission_shudder],
    ['suspension_alterations', extSubs.suspension_alterations],
    ['suspension_operations', extSubs.suspension_operations],
    ['exhaust_sounds_factory', extSubs.exhaust_sounds_factory],
    ['catalytic_converter_present', extSubs.catalytic_converter_present],
    ['3_point_turn_cv_reserve_test', dcSubs['3_point_turn_cv_reserve_test']],
    ['vehicle_breaking', dcSubs.vehicle_breaking],
    ...(isManual
      ? ([
          ['position_of_clutch_pedal', dcSubs.position_of_clutch_pedal],
          ['clutch_engages_in_gear', dcSubs.clutch_engages_in_gear],
          ['manual_transmission_engages_every_gear', dcSubs.manual_transmission_engages_every_gear],
        ] as Array<[string, Section | undefined]>)
      : []),
    ...(is4WD
      ? ([
          ['engage_in_low_4x4', dcSubs.engage_in_low_4x4],
          ['engage_in_high_4x4', dcSubs.engage_in_high_4x4],
        ] as Array<[string, Section | undefined]>)
      : []),
    ['how_engine_runs_overall', dcSubs.how_engine_runs_overall],
  ];

  const subsections: Record<string, Section> = {};
  candidates.forEach(([key, sub]) => {
    if (sub?.grade) subsections[key] = sub;
  });

  const rawSection = sections?.drive_cycles;
  if (!rawSection) return undefined;

  return { ...rawSection, subsections };
};
